import { usePortalMessage } from "../../i18n/message.js";
import { portalText, usePortalTranslation } from "../../i18n/portal.js";
/**
 * "Request more lines" — ask WeTalk for more concurrent conversations (customer-portal review #115
 * and #59, fix round 2026-09-25).
 *
 * Every concurrency action in the console used to lead to Billing, where the Tier card said
 * asking "is not open yet, so write to us" with nowhere to write. This dialog records a
 * `quota_request` (`POST /v1/quota-requests`); an operator reads it and raises the account's limit.
 * It is used from Billing's Tier card and from the agent's Channels tab, so both lead somewhere
 * that can act. The copy says what happens next and promises no turnaround nobody has committed to.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Dialog, Field, Input, Textarea } from "@wetalk/ui";
import { useState, type ReactNode } from "react";

import { apiErrorText } from "../../api/api-error.js";
import { useGateway } from "../../api/provider.js";
import { QUERY_KEY } from "../../api/query.js";
import type { ConsoleGateway } from "../../api/gateway.js";
import type { QuotaRequestView } from "../../api/slice/pending-request.js";

/** The most a request may ask for — the API's own bound. */
export const QUOTA_REQUEST_MAX = 500;

export const quotaRequestListQuery = (gateway: ConsoleGateway) => ({
  queryKey: QUERY_KEY.quotaRequest,
  queryFn: () => gateway.listQuotaRequests(),
});

export interface QuotaRequestDialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
  /** Today's limit, when known — the form proposes one more than it. */
  readonly currentCeiling: number | null;
}

export function QuotaRequestDialog({ open, onClose, currentCeiling }: QuotaRequestDialogProps): ReactNode {
  usePortalTranslation("finance");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const [countText, setCountText] = useState(currentCeiling === null ? "" : String(currentCeiling + 5));
  const [justification, setJustification] = useState("");
  const [problem, setProblem] = usePortalMessage();
  const [sent, setSent] = useState<QuotaRequestView | null>(null);

  const send = useMutation({
    mutationFn: (requestedChannelUnit: number) =>
      gateway.requestConcurrencyQuota({ requestedChannelUnit, justification }),
    onSuccess: async (made) => {
      setSent(made);
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY.quotaRequest });
    },
    onError: (error: unknown) => {
      setProblem(() => (apiErrorText(error, portalText("finance", "m0314"))));
    },
  });

  const close = (): void => {
    setProblem(null);
    setSent(null);
    onClose();
  };

  // Mounted only while open: the dialog is offered from more than one place on a page (the
  // Channels tab has two), and two closed copies would put two identical forms in the document.
  if (!open) return null;

  const submit = (): void => {
    const count = Number(countText.trim());
    if (!Number.isInteger(count) || count < 1 || count > QUOTA_REQUEST_MAX) {
      setProblem(() => (portalText("finance", "m0315", { value1: String(QUOTA_REQUEST_MAX) })));
      return;
    }
    setProblem(null);
    send.mutate(count);
  };

  return (
    <Dialog
      open={open}
      title={portalText("finance", "m0316")}
      onClose={close}
      actions={
        <>
          <Button variant="secondary" onClick={close}>
            {sent === null ? portalText("finance", "m0106") : portalText("finance", "m0107")}
          </Button>
          {sent === null ? (
            <Button variant="primary" disabled={send.isPending} onClick={submit}>
              {send.isPending ? portalText("finance", "m0317") : portalText("finance", "m0318")}
            </Button>
          ) : null}
        </>
      }
    >
      {sent !== null ? (
        <p className="c-note" role="status">{portalText("finance", "m0319")}{String(sent.requestedChannelUnit)}{portalText("finance", "m0320")}</p>
      ) : (
        <>
          <p className="c-note">
            {currentCeiling === null
              ? portalText("finance", "m0321")
              : portalText("finance", "m0322", { value1: String(currentCeiling) })}{" "}{portalText("finance", "m0323")}</p>
          <Field label={portalText("finance", "m0324")} {...(problem === null ? {} : { error: problem })}>
            {(control) => (
              <Input
                {...control}
                inputMode="numeric"
                value={countText}
                onChange={(event) => {
                  const next = event.currentTarget.value;
                  setCountText(next);
                }}
              />
            )}
          </Field>
          <Field label={portalText("finance", "m0325")} description={portalText("finance", "m0326")}>
            {(control) => (
              <Textarea
                {...control}
                rows={3}
                maxLength={2000}
                value={justification}
                onChange={(event) => {
                  const next = event.currentTarget.value;
                  setJustification(next);
                }}
              />
            )}
          </Field>
        </>
      )}
    </Dialog>
  );
}

/** The pending requests, one line each, so a second click does not look like the first was lost. */
export function PendingQuotaRequestLine(): ReactNode {
  usePortalTranslation("finance");
  const gateway = useGateway();
  const listed = useQuery(quotaRequestListQuery(gateway));
  const pending = (listed.data ?? []).filter((one) => one.state === "pending");
  const latest = pending[0];
  if (latest === undefined) return null;
  return (
    <span className="c-note" role="status">{portalText("finance", "m0327")}{String(latest.requestedChannelUnit)}{portalText("finance", "m0328")}{latest.requestedAtText}{portalText("finance", "m0329")}</span>
  );
}
